/**
 * events.ts — обработчики событий click, change, touch highlight
 * Проект: TorgiRT (shared — все витрины)
 *
 * Архитектура: Action Map (объект-маршрутизатор).
 * handleClick / handleChange — компактные диспетчеры (~15 строк каждый),
 * логика каждого действия — изолированная функция в объекте actions / changeActions.
 */

import { state, refs, UI_TIMING, VIEW_MODE_KEY, productLookup, BASE_PATH, ITEMS_PER_PAGE } from './state';
import { formatPrice, SF_CURRENCY_HTML, isCartAllowedPage, isCatalogListingPage, isOnZakazPage, resolveCatalogCatDbId, navigateToCatalogCat, getConfig, getCartTotal, getCartUnitsCount, getCartQuantity, scrollBehavior, imageSrcMatches, setPictureImageSrc } from './helpers';
import { runWithQtyFocusRestore, restoreQtyFocusAfterDomUpdate, captureQtyFocus } from './qty-keyboard';
import { handleStockAlertClick } from './stock-alert';
import { loadMoreReviews } from './reviews';
import {
  addToCart, updateQuantity, withScrollCompensation,
  toggleCartAnimated, scrollToCartArea,
  saveCartToStorage, scheduleSyncToServer, syncCartToServer,
} from './cart';
import { selectCategory, selectSubcategory, fetchProducts } from './catalog';
import { runCatalogFetchThen } from './catalog-loading';
import { pushUrlState } from './url';
import { applySearch, showSearchOverlay, hideSearchOverlay } from './search';
import {
  render, renderCatalog, renderProductsCount, renderProducts,
  renderPagination, renderFilters, renderModal, renderViewModeButtons,
  renderCartBadge, renderCartSummary, renderCartItems, updateModalImage,
  updateProductPageGallery,
  updateProductCard, updateModalActions, updateFavButton, markClampedTitles,
  showToast, announceCartChange,
} from './render';
import { showFlash } from './toast';
import { openProductModal, closeProductModal, shiftModalImage } from './modal';
import { toggleFavorite, getFavorites, scheduleFavSync } from './favorites';
import { navigateToProduct } from './navigation';
import { updateFloatingCartText, updateFloatingCartVisibility } from './floating-cart';

/* ══════════════════════════════════════════════════════════════
   Вспомогательные функции (устраняют дублирование)
   ══════════════════════════════════════════════════════════════ */

const afterCatalogFetch = runCatalogFetchThen;

const addToCartGuard = new Set<string>();

function cartLineAnnounce(article: string, kind: 'added' | 'removed' | 'changed'): void {
  const lineQty = getCartQuantity(article);
  const total = getCartUnitsCount();
  if (kind === 'added') {
    announceCartChange(`Товар добавлен в корзину. Количество ${lineQty}, всего в корзине ${total}`);
    return;
  }
  if (kind === 'removed') {
    announceCartChange(`Товар удалён из корзины. Всего в корзине ${total}`);
    return;
  }
  announceCartChange(`Количество изменено: ${lineQty}. Всего в корзине ${total}`);
}

/** После смены страницы каталога — начало списка под sticky-шапкой */
function scrollToProductListTop(): void {
  const products = document.getElementById("products");
  if (!products) return;
  const header = document.querySelector(".site-header");
  const headerH = header?.getBoundingClientRect().height ?? 0;
  const top =
    products.getBoundingClientRect().top + window.scrollY - headerH - 8;
  window.scrollTo({ top: Math.max(0, top), behavior: "auto" });
}

/** Перерисовать счётчик + список товаров + пагинацию */
function renderProductList(): void {
  renderProductsCount();
  renderProducts();
  renderPagination();
  markClampedTitles();
}

/** Перерисовать список товаров + счётчик + пагинацию + фильтры */
function renderFilteredView(): void {
  renderProductList();
  renderFilters();
}

/** Перерисовать корзину + точечно обновить карточку товара (Фаза 3) */
function renderCartUpdate(article: string | undefined): void {
  renderCartBadge();
  renderCartSummary();
  renderCartItems();
  if (article) {
    updateProductCard(article);
    updateModalActions(article);
  } else {
    renderProducts();
    markClampedTitles();
  }
  updateFloatingCartText();
  updateFloatingCartVisibility();
}

/** Сброс категории, подкатегории, фильтров — для поиска во всём каталоге */
function resetCatalogContext(): void {
  state.selectedCategory = null;
  state.selectedSubcategory = null;
  state.breadcrumbPath = null;
  state.currentFilters = [];
  state.activeFilters = {};
  state.currentPage = 1;
}

/** Кнопки +/- на серверной странице поиска (прямая DOM-манипуляция) */
function handleServerPageQty(el: HTMLElement, delta: number): void {
  const row = el.closest("[data-article], [data-product-id]") as HTMLElement | null;
  if (!row) return;
  const focusSnapshot = captureQtyFocus(el);
  const article = row.dataset.article;
  if (!article) return;
  const stockVal = Number(row.dataset.stock || 0);
  const syncIncreaseButtonState = function (qty: number): void {
    const increaseBtn = row.querySelector('[data-action="increase-qty"]') as HTMLButtonElement | null;
    if (!increaseBtn) return;
    const atLimit = stockVal > 0 && qty >= stockVal;
    increaseBtn.disabled = atLimit;
    if (atLimit) {
      increaseBtn.title = `Доступно ${stockVal} шт.`;
    } else {
      increaseBtn.removeAttribute("title");
    }
  };

  withScrollCompensation(function () {
    const existing = state.cart.find(function (item: any) { return item.article === article; });
    const prevQty = existing ? existing.quantity : 0;
    if (existing) {
      if (delta > 0 && stockVal > 0 && existing.quantity >= stockVal) {
        showToast(`Доступно ${stockVal} шт.`);
        syncIncreaseButtonState(existing.quantity);
      } else {
        updateQuantity(article, delta);
      }
    } else if (delta > 0) {
      if (stockVal <= 0) {
        // Нет в наличии — не добавляем
      } else {
        state.cart.push({
          article,
          name: row.dataset.name || article,
          price: Number(row.dataset.price || 0),
          unit: row.dataset.unit || "шт.",
          stock: stockVal,
          quantity: 1,
          images: [],
          filters: {},
        } as any);
        saveCartToStorage();
        scheduleSyncToServer();
      }
    }
    const cartWidget = row.closest("#korzina-wrapper");
    const cartItem = state.cart.find(function (item: any) { return item.article === article; });
    const qty = cartItem ? cartItem.quantity : 0;
    syncIncreaseButtonState(qty);

    if (cartWidget) {
      if (qty <= 0) {
        row.remove();
        const remainingRows = cartWidget.querySelectorAll(".list-row");
        if (remainingRows.length === 0) {
          // Без reload: иначе SSR из session опережает sync и товар «воскресает»
          const basePath = String(getConfig("basePath", "/i") || "/i");
          const emptyHtml =
            `<div class="card cart-empty">` +
            `<p class="cart-empty-title">Корзина пуста</p>` +
            `<p class="cart-empty-text">Добавьте товары из каталога</p>` +
            `<a href="${basePath}/" class="btn btn-accent">Перейти в каталог</a>` +
            `</div>`;
          const footer = document.querySelector(".cart-footer");
          if (footer) footer.remove();
          cartWidget.outerHTML = emptyHtml;
          void syncCartToServer();
          renderCartBadge();
          return;
        }
      } else {
        const qtyEl = row.querySelector(".qty-value");
        if (qtyEl) qtyEl.textContent = String(qty);
        const totalCell = row.querySelector(".total-cell");
        if (totalCell) {
          const price = Number(row.dataset.price || 0);
          totalCell.innerHTML = `<span class="list-total">${formatPrice(
            price * qty
          )}</span>${SF_CURRENCY_HTML}`;
        }
      }
      const totalEl = document.querySelector(".cart-total-amount-widget");
      if (totalEl) {
        totalEl.textContent = formatPrice(getCartTotal());
      }
    } else {
      const qtyEl = row.querySelector(".qty-value");
      if (qtyEl) qtyEl.textContent = String(qty);
      const totalCell = row.querySelector(".total-cell");
      if (totalCell) {
        const priceEl = row.querySelector(".list-price-amount");
        const price = priceEl
          ? parseFloat((priceEl.textContent || "").replace(/\s/g, ""))
          : 0;
        if (qty > 0) {
          totalCell.innerHTML = `<span class="list-total">${formatPrice(
            price * qty
          )}</span>${SF_CURRENCY_HTML}`;
        } else {
          totalCell.innerHTML = "";
        }
      }
    }

    renderCartBadge();
    renderCartSummary();
    renderCartItems();
    updateFloatingCartText();
    updateFloatingCartVisibility();
    queueMicrotask(() => {
      restoreQtyFocusAfterDomUpdate(focusSnapshot);
    });
    if (qty !== prevQty) {
      if (qty === 0) {
        cartLineAnnounce(article, 'removed');
      } else if (prevQty === 0) {
        cartLineAnnounce(article, 'added');
      } else {
        cartLineAnnounce(article, 'changed');
      }
    }
  });
}

/** UX-I2/R2: sync cart to session before navigating to /zakaz (button + header/drawer links). */
async function navigateToZakaz(trigger?: HTMLElement): Promise<void> {
  const zakazUrl = getConfig("zakazRouterUrl", "/i/zakaz") ?? "/i/zakaz";

  if (state.cart.length === 0) {
    showToast("Корзина пуста");
    return;
  }

  const isButton = trigger instanceof HTMLButtonElement;
  const link = trigger instanceof HTMLAnchorElement ? trigger : null;
  const prevText = link?.textContent ?? null;

  const resetTrigger = (): void => {
    if (isButton && trigger) {
      trigger.disabled = false;
      trigger.textContent = "Оформить заказ";
    } else if (link) {
      link.classList.remove("is-zakaz-loading");
      link.removeAttribute("aria-busy");
      if (prevText !== null) link.textContent = prevText;
    }
  };

  if (isButton) {
    trigger.disabled = true;
    trigger.textContent = "Загрузка…";
  } else if (link) {
    link.classList.add("is-zakaz-loading");
    link.setAttribute("aria-busy", "true");
  }

  try {
    const res = await syncCartToServer();
    if (!res || !(res as Response).ok) throw new Error("sync failed");
    if (state.cart.length === 0) {
      showToast("Корзина пуста");
      resetTrigger();
      return;
    }
    window.location.href = zakazUrl;
    return;
  } catch {
    showFlash("Не удалось сохранить корзину. Проверьте интернет и попробуйте снова.", 'error');
  }

  resetTrigger();
}

/* ══════════════════════════════════════════════════════════════
   Action Map — click-действия (29 записей)
   ══════════════════════════════════════════════════════════════ */

const actions: Record<string, (target: HTMLElement, event: Event) => void | Promise<void>> = {
  /* ── Каталог ── */

  "toggle-catalog"() {
    state.catalogOpen = !state.catalogOpen;
    renderCatalog();
  },

  "toggle-search-overlay"() {
    showSearchOverlay();
  },

  "close-search-overlay"() {
    hideSearchOverlay();
  },

  async "select-category"(el: HTMLElement, e: Event) {
    e.preventDefault();
    const categoryId = el.dataset.categoryId;
    if (!categoryId) return;
    // Touch-устройство: первый тап только раскрывает подкатегории
    const isTouchDevice = window.matchMedia("(hover: none)").matches;
    if (isTouchDevice && state.catalogOpen && !state.expandedCategories.has(categoryId)) {
      state.expandedCategories.clear();
      state.expandedCategories.add(categoryId);
      renderCatalog();
      return;
    }
    if (!isCatalogListingPage()) {
      const dbId = resolveCatalogCatDbId(categoryId);
      if (dbId == null) return;
      navigateToCatalogCat(dbId);
      return;
    }
    await afterCatalogFetch(() => selectCategory(categoryId), () => {
      pushUrlState();
      render();
    });
  },

  async "select-subcategory"(el: HTMLElement, e: Event) {
    e.preventDefault();
    const { categoryId, subcategoryId } = el.dataset;
    if (!categoryId || !subcategoryId) return;
    if (!isCatalogListingPage()) {
      const dbId = resolveCatalogCatDbId(categoryId, subcategoryId);
      if (dbId == null) return;
      navigateToCatalogCat(dbId);
      return;
    }
    await afterCatalogFetch(
      () => selectSubcategory(categoryId, subcategoryId),
      () => {
        pushUrlState();
        render();
      },
    );
  },

  async "reset-catalog"(el: HTMLElement, e: Event) {
    e.preventDefault();
    resetCatalogContext();
    state.sortBy = "";
    state.searchMode = "strict";
    state.searchQuery = "";
    if (refs.searchInput) (refs.searchInput as HTMLInputElement).value = "";
    await afterCatalogFetch(() => fetchProducts({ page: 1 }), () => {
      pushUrlState();
      render();
    });
  },

  async "retry-fetch"() {
    state.loadError = false;
    await afterCatalogFetch(() => fetchProducts(), () => {
      renderProductList();
    });
  },

  /* ── Поиск / фильтры ── */

  async "clear-search"(el: HTMLElement, e: Event) {
    e.preventDefault();
    state.searchQuery = "";
    state.searchMode = "strict";
    if (refs.searchInput) (refs.searchInput as HTMLInputElement).value = "";
    state.currentPage = 1;
    await afterCatalogFetch(() => fetchProducts({ q: "", page: 1 }), () => {
      pushUrlState();
      renderFilteredView();
    });
  },

  async "reset-filters"(el: HTMLElement, e: Event) {
    e.preventDefault();
    state.activeFilters = {};
    state.sortBy = "";
    state.currentPage = 1;
    await afterCatalogFetch(() => fetchProducts({ filters: {}, sort: "", page: 1 }), () => {
      pushUrlState();
      renderFilteredView();
    });
  },

  async "search-global"(el: HTMLElement, e: Event) {
    e.preventDefault();
    resetCatalogContext();
    state.searchMode = "strict";
    await afterCatalogFetch(() => fetchProducts({ page: 1 }), () => {
      pushUrlState();
      render();
    });
  },

  async "search-partial"(el: HTMLElement, e: Event) {
    e.preventDefault();
    // При серверной пагинации partial-режим не поддерживается — делаем обычный поиск
    resetCatalogContext();
    state.searchMode = "strict";
    await afterCatalogFetch(() => fetchProducts({ page: 1 }), () => {
      pushUrlState();
      render();
    });
  },

  async "search-suggest"(el: HTMLElement, e: Event) {
    e.preventDefault();
    const suggestedText = el.dataset.suggest;
    if (!suggestedText) return;
    state.searchQuery = suggestedText;
    if (refs.searchInput) (refs.searchInput as HTMLInputElement).value = suggestedText;
    resetCatalogContext();
    state.searchMode = "strict";
    await afterCatalogFetch(() => fetchProducts({ q: suggestedText, page: 1 }), () => {
      pushUrlState();
      render();
    });
  },

  "do-search"() {
    applySearch(true);
  },

  /* ── Отображение ── */

  "set-view"(el: HTMLElement) {
    const mode = el.dataset.mode;
    if (mode !== "grid" && mode !== "list") return;
    state.viewMode = mode;
    localStorage.setItem(VIEW_MODE_KEY, mode);
    renderViewModeButtons();
    renderProducts();
    renderCartItems();
    markClampedTitles();
  },

  async "set-page"(el: HTMLElement) {
    const page = Number(el.dataset.page ?? "1");
    if (!Number.isNaN(page) && page > 0) {
      (document.activeElement as HTMLElement | null)?.blur?.();
      state.currentPage = page;
      await afterCatalogFetch(() => fetchProducts({ page }), () => {
        pushUrlState();
        renderProductList();
        scrollToProductListTop();
      });
    }
  },

  "load-more-reviews"(el: HTMLElement) {
    void loadMoreReviews(el);
  },

  /* ── Корзина ── */

  "stock-alert"(el: HTMLElement) {
    handleStockAlertClick(el);
  },

  "add-to-cart"(el: HTMLElement) {
    const article = el.dataset.article;
    if (!article || addToCartGuard.has(article)) return;
    addToCartGuard.add(article);
    runWithQtyFocusRestore(el, () => {
      withScrollCompensation(function () { addToCart(article); renderCartUpdate(article); });
    });
    cartLineAnnounce(article, 'added');
    queueMicrotask(() => { addToCartGuard.delete(article); });
  },

  "modal-add-to-cart"(el: HTMLElement) {
    const article = el.dataset.article;
    if (!article) return;
    runWithQtyFocusRestore(el, () => {
      addToCart(article);
      renderCartUpdate(article);
    });
    cartLineAnnounce(article, 'added');
  },

  "change-qty"(el: HTMLElement) {
    const article = el.dataset.article;
    const delta = Number(el.dataset.delta ?? "0");
    if (!article || Number.isNaN(delta) || delta === 0) return;
    const hadItem = state.cart.some((i: any) => i.article === article);
    runWithQtyFocusRestore(el, () => {
      withScrollCompensation(function () { updateQuantity(article, delta); renderCartUpdate(article); });
    });
    const hasItem = state.cart.some((i: any) => i.article === article);
    if (hadItem && !hasItem) {
      cartLineAnnounce(article, 'removed');
    } else {
      cartLineAnnounce(article, 'changed');
    }
  },

  "increase-qty"(el: HTMLElement) { handleServerPageQty(el, 1); },
  "decrease-qty"(el: HTMLElement) { handleServerPageQty(el, -1); },

  "toggle-cart-summary"() { toggleCartAnimated(); },
  "toggle-cart-floating"() { scrollToCartArea(); },

  async "go-to-zakaz"(el: HTMLElement) {
    await navigateToZakaz(el);
  },

  /* ── Модальное окно товара ── */

  "toggle-favorite"(el: HTMLElement) {
    const article = el.dataset.article;
    if (!article) return;
    toggleFavorite(article);
    updateFavButton(article);
    scheduleFavSync(); // Этап 2: серверная синхронизация (no-op для гостей)
  },

  "open-product"(el: HTMLElement) {
    const article = el.dataset.article;
    if (!article) return;
    openProductModal(article);
    if (state.selectedProduct) renderModal();
  },

  "close-modal"() {
    closeProductModal();
    renderModal();
  },

  "open-image-viewer"() {
    if (state.selectedProduct?.images?.length) {
      state.imageViewerOpen = true;
      renderModal();
    }
  },

  "close-image-viewer"() {
    state.imageViewerOpen = false;
    if (state.productPage) {
      closeProductModal();
    }
    renderModal();
  },

  "viewer-prev-image"() { shiftModalImage(-1); updateModalImage(); },
  "viewer-next-image"() { shiftModalImage(1); updateModalImage(); },

  "modal-prev-image"() {
    if (!state.selectedProduct) return;
    const len = state.selectedProduct.images.length;
    if (len > 0) {
      state.currentImageIndex =
        state.currentImageIndex === 0 ? len - 1 : state.currentImageIndex - 1;
      updateModalImage();
    }
  },

  "modal-next-image"() {
    if (!state.selectedProduct) return;
    const len = state.selectedProduct.images.length;
    if (len > 0) {
      state.currentImageIndex =
        state.currentImageIndex === len - 1 ? 0 : state.currentImageIndex + 1;
      updateModalImage();
    }
  },

  "modal-set-image"(el: HTMLElement) {
    const idx = Number(el.dataset.imageIndex ?? "0");
    if (!Number.isNaN(idx)) {
      state.currentImageIndex = idx;
      updateModalImage();
    }
  },

  /* ── Просмотр изображений на странице товара ── */

  "open-product-page-viewer"(el: HTMLElement) {
    const article = el.dataset.article;
    if (!article) return;
    const product = productLookup.get(article);
    if (!product || !product.images?.length) return;

    let idx = state.productPageImageIndex;
    if (idx < 0 || idx >= product.images.length) {
      const mainImg = document.getElementById("product-main-image") as HTMLImageElement | null;
      idx = 0;
      if (mainImg) {
        const currentSrc = mainImg.currentSrc || mainImg.src;
        const found = product.images.findIndex((img: string) => imageSrcMatches(currentSrc, img));
        if (found >= 0) idx = found;
      }
    }

    state.selectedProduct = product;
    state.currentImageIndex = idx;
    state.imageViewerOpen = true;
    renderModal();
  },

  "product-page-prev-image"() {
    if (!state.productPage?.images?.length) return;
    const len = state.productPage.images.length;
    state.productPageImageIndex =
      state.productPageImageIndex === 0 ? len - 1 : state.productPageImageIndex - 1;
    updateProductPageGallery();
  },

  "product-page-next-image"() {
    if (!state.productPage?.images?.length) return;
    const len = state.productPage.images.length;
    state.productPageImageIndex =
      state.productPageImageIndex === len - 1 ? 0 : state.productPageImageIndex + 1;
    updateProductPageGallery();
  },

  /* ── Миниатюры на странице товара ── */

  "product-page-set-thumb"(el: HTMLElement) {
    const idx = Number(el.dataset.index ?? "0");
    if (Number.isNaN(idx) || !state.productPage?.images?.length) return;
    state.productPageImageIndex = idx;
    updateProductPageGallery();
  },

  /* ── Bottom navigation (H.5) ── */

  async 'bottom-nav'(el: HTMLElement) {
    const target = el.dataset.target ?? el.closest('[data-target]')?.getAttribute('data-target') ?? '';
    switch (target) {
      case 'catalog':
        if (isCatalogListingPage()) {
          window.scrollTo({ top: 0, behavior: scrollBehavior() });
          state.catalogOpen = true;
          renderCatalog();
        } else {
          window.location.href = BASE_PATH + '/';
        }
        break;
      case 'favorites':
        state.favoritesOnly = !state.favoritesOnly;
        state.currentPage = 1;
        await afterCatalogFetch(
          () => fetchProducts({
            favorites: state.favoritesOnly ? getFavorites().join(',') : '',
            page: 1,
          }),
          () => {
            render();
            pushUrlState();
          },
        );
        break;
      case 'cart':
        state.cartOpen = !state.cartOpen;
        renderCartSummary();
        renderCartItems();
        if (state.cartOpen && refs.cartSummary) {
          refs.cartSummary.scrollIntoView({ behavior: scrollBehavior() });
        }
        break;
    }
  },

  /* ── Search hint (H.1) ── */

  async 'search-hint'(el: HTMLElement) {
    const hint = el.dataset.hint ?? '';
    if (!hint || !refs.searchInput) return;
    (refs.searchInput as HTMLInputElement).value = hint;
    applySearch(true);
  },
};

/* ══════════════════════════════════════════════════════════════
   Action Map — change-действия (3 записи)
   ══════════════════════════════════════════════════════════════ */

const changeActions: Record<string, (target: HTMLElement, event: Event) => void | Promise<void>> = {
  async "set-sort"(el: HTMLElement) {
    state.sortBy = (el as HTMLSelectElement).value;
    state.currentPage = 1;
    const sortVal = (el as HTMLSelectElement).value;
    await afterCatalogFetch(() => fetchProducts({ sort: sortVal, page: 1 }), () => {
      pushUrlState();
      renderProductList();
    });
  },

  async "toggle-stock"(el: HTMLElement) {
    state.stockOnly = (el as HTMLInputElement).checked;
    state.currentPage = 1;
    const checked = (el as HTMLInputElement).checked;
    await afterCatalogFetch(() => fetchProducts({ stock_only: checked, page: 1 }), () => {
      pushUrlState();
      renderProductList();
    });
  },

  async "toggle-favorites-only"(el: HTMLElement) {
    state.favoritesOnly = (el as HTMLInputElement).checked;
    state.currentPage = 1;
    if ((el as HTMLInputElement).checked) {
      const favs = getFavorites();
      if (favs.length === 0) {
        state.products = [];
        state.pagination = { total: 0, page: 1, perPage: ITEMS_PER_PAGE, totalPages: 1 } as any;
        renderProductList();
        pushUrlState();
        return;
      }
      await afterCatalogFetch(
        () => fetchProducts({ favorites: favs.join(","), page: 1 }),
        () => {
          pushUrlState();
          renderProductList();
        },
      );
      return;
    }
    await afterCatalogFetch(() => fetchProducts({ page: 1 }), () => {
      pushUrlState();
      renderProductList();
    });
  },

  async "set-filter"(el: HTMLElement) {
    const filterName = el.dataset.filterName;
    const filterValue = (el as HTMLSelectElement).value;
    if (!filterName) return;

    if (filterValue) {
      state.activeFilters[filterName] = filterValue;
    } else {
      delete state.activeFilters[filterName];
    }

    state.currentPage = 1;
    await afterCatalogFetch(() => fetchProducts({ page: 1 }), () => {
      pushUrlState();
      renderProductList();
    });
  },
};

/* ══════════════════════════════════════════════════════════════
   Экспортируемые обработчики (API для main.js)
   ══════════════════════════════════════════════════════════════ */

export function handleClick(event: Event): void {
  // SPA-переход по ссылке на товар
  const productLink = (event.target as HTMLElement).closest('a.product-page-link, a.modal-product-link, a.pdp-cross-sell__more');
  if (productLink) {
    const href = (productLink as HTMLAnchorElement).href;
    const match = href.match(/\/product\/([^/?#]+)/);
    if (match) {
      event.preventDefault();
      const article = decodeURIComponent(match[1]);
      void navigateToProduct(article);
      return;
    }
  }

  // Иконка корзины — <a href="…/" class="cart-button">; на catalog-shell — toggle, иначе переход на каталог
  const cartBtn = (event.target as HTMLElement).closest(".cart-button");
  if (cartBtn) {
    if (!isCartAllowedPage()) return;
    event.preventDefault();
    if (isOnZakazPage()) {
      document.getElementById("zakaz-cart-anchor")?.scrollIntoView({ behavior: scrollBehavior() });
      return;
    }
    toggleCartAnimated();
    return;
  }

  const zakazLink = (event.target as HTMLElement).closest('a[href*="/zakaz"]') as HTMLAnchorElement | null;
  if (zakazLink) {
    event.preventDefault();
    void navigateToZakaz(zakazLink);
    return;
  }

  const el = (event.target as HTMLElement).closest("[data-action]") as HTMLElement | null;
  if (!el) {
    // Закрытие каталога при клике снаружи
    if (state.catalogOpen && refs.catalogWrap && !refs.catalogWrap.contains(event.target as Node)) {
      state.catalogOpen = false;
      renderCatalog();
    }
    return;
  }

  const handler = actions[el.dataset.action!];
  if (handler) void handler(el, event);
}

/** Keyboard activation for non-native focusable [data-action] (e.g. open-product). */
export function handleKeydown(event: KeyboardEvent): void {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  const el = (event.target as HTMLElement).closest('[data-action]') as HTMLElement | null;
  if (!el || el.tagName === 'BUTTON' || el.tagName === 'A' || el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
    return;
  }
  if (!el.hasAttribute('tabindex')) return;
  event.preventDefault();
  handleClick(event);
}

export function handleChange(event: Event): void {
  const el = (event.target as HTMLElement).closest("[data-action]") as HTMLElement | null;
  if (!el) return;

  const handler = changeActions[el.dataset.action!];
  if (handler) void handler(el, event);
}

/* ── Touch: tap-only подсветка строки списка (без срабатывания при скролле) ── */
export function initListRowTouchHighlight(): void {
  if (!window.matchMedia("(hover: none) and (pointer: coarse)").matches) return;

  const MOVE_THRESHOLD = 10; // px — порог, после которого считаем скроллом
  let touchState: { row: HTMLElement; x: number; y: number; moved: boolean } | null = null;

  document.addEventListener("pointerdown", function (e: PointerEvent) {
    if (e.pointerType !== "touch") return;
    const row = (e.target as HTMLElement).closest(".products-list .list-row, .cart-list .list-row") as HTMLElement | null;
    if (!row) return;
    touchState = { row: row, x: e.clientX, y: e.clientY, moved: false };
    row.classList.add("is-touch-active");
  }, { passive: true });

  document.addEventListener("pointermove", function (e: PointerEvent) {
    if (!touchState || e.pointerType !== "touch") return;
    const dx = Math.abs(e.clientX - touchState.x);
    const dy = Math.abs(e.clientY - touchState.y);
    if (dx > MOVE_THRESHOLD || dy > MOVE_THRESHOLD) {
      touchState.moved = true;
      touchState.row.classList.remove("is-touch-active");
    }
  }, { passive: true });

  function clear(): void {
    if (!touchState) return;
    touchState.row.classList.remove("is-touch-active");
    touchState = null;
  }

  document.addEventListener("pointerup", function (e: PointerEvent) {
    if (!touchState || e.pointerType !== "touch") return;
    const row = touchState.row;
    const moved = touchState.moved;
    touchState = null;
    if (!moved) {
      setTimeout(function () { row.classList.remove("is-touch-active"); }, UI_TIMING.tapFeedback);
    } else {
      row.classList.remove("is-touch-active");
    }
  }, { passive: true });

  document.addEventListener("pointercancel", clear, { passive: true });
}

/* ── AJAX gallery: arrow click + touch-swipe ── */
export function initAjaxGallery(): void {
  document.addEventListener("click", function (e: Event) {
    const btn = (e.target as HTMLElement).closest(".buts_l, .buts_r");
    if (!btn) return;
    const slider = btn.parentElement!.querySelector(".tovslid") as HTMLElement | null;
    if (!slider) return;
    const imgs = slider.querySelectorAll("img");
    if (imgs.length < 2) return;
    let current = slider.dataset.current ? parseInt(slider.dataset.current, 10) : 0;
    current += btn.classList.contains("buts_r") ? 1 : -1;
    if (current < 0) current = imgs.length - 1;
    if (current >= imgs.length) current = 0;
    slider.style.transform = "translateX(-" + (current * 100) + "%)";
    slider.dataset.current = String(current);
  });

  document.addEventListener("touchstart", function (e: TouchEvent) {
    const gallery = (e.target as HTMLElement).closest(".tovpho") as HTMLElement | null;
    if (gallery) (gallery as any)._swipeStartX = e.changedTouches[0].clientX;
  }, { passive: true });

  document.addEventListener("touchend", function (e: TouchEvent) {
    const gallery = (e.target as HTMLElement).closest(".tovpho") as HTMLElement | null;
    if (!gallery || (gallery as any)._swipeStartX === undefined) return;
    const dx = e.changedTouches[0].clientX - (gallery as any)._swipeStartX;
    if (Math.abs(dx) > 50) {
      const btn = gallery.querySelector(dx < 0 ? ".buts_r" : ".buts_l") as HTMLElement | null;
      if (btn) btn.click();
    }
    delete (gallery as any)._swipeStartX;
  }, { passive: true });
}
